import React from "react";
import {
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Tooltip,
  Divider,
  Row,
  Col,
  Card,
  Space,
  Typography,
  type FormInstance,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { clpInputFormatter, clpInputParser } from "../../../utils/clpInput";
import RepartoAseadorasField from "../../aseos/components/RepartoAseadorasField";
import {
  StartThirdField,
  EndThirdField,
  StartTimeField,
  EndTimeField,
} from "./ThirdAndTimeFields";
import type { Third } from "../calendarCreateModal.helpers";
import AseoNotesSection from "../../aseos/components/AseoNotesSection";

const { Text } = Typography;

interface CleaningFieldsProps {
  form: FormInstance;
  startThirdWatch: Third;
  endThirdWatch: Third;
  modoAsignacion: string | undefined;
  aseoCostPreset: string | undefined;
  /**
   * Cambio MANUAL del Modo del desglose. El modal (dueño del form) hace acá
   * el snapshot/repintado de los 4 inputs custom — en el onChange y no en un
   * efecto reactivo, para que la hidratación en edición nunca se pise
   * (round 2026-08-02). Ver handleAseoPresetChange en calendarCreateModal.
   */
  onAseoPresetChange?: (next: "default" | "custom") => void;
  aseoCostoTotal: number;
  // `rolAFWatch` eliminado en round 2026-05-13 — el aseo ya no lleva
  // `rolAF`, el único campo de flujo es `pagado_por`.
  esPasado: boolean;
  esFuturo: boolean;
  loadingAseadoras: boolean;
  aseadorOptions: Array<{ value: number; label: string }>;
  ingreso: any;
  range:
    | {
        propertyId: number;
        [k: string]: any;
      }
    | null
    | undefined;
  isDayFullyOccupied?:
    | ((propertyId: number, dayISO: string) => boolean)
    | null;
  /**
   * true si el aseo editado está VINCULADO a un arriendo (reserva_id ≠ null).
   * En ese caso la limpieza ya se le descuenta al cliente en la liquidación,
   * así que la opción "Propietario/Cliente paga" (tipo=1) se deshabilita para
   * evitar el doble cobro — misma regla que /aseos y /lavandería. El backend
   * es la red final (rechaza 400), esto es solo UX. Ver flujo-financiero §6.4.
   */
  esVinculado?: boolean;
  /**
   * "create" | "edit". En EDICIÓN el aseo ya existe (tiene id) → embebemos el
   * editor de las 3 notas (texto + fotos/videos/documentos, guardado inmediato).
   * En CREACIÓN aún no hay id: se muestra solo el texto de "Nota para el
   * aseador" (mapea a la columna `observaciones`, que el payload del calendario
   * ya envía); las demás notas y los archivos se agregan editando el aseo.
   * Round 2026-07-06.
   */
  mode?: "create" | "edit";
  /** Id del aseo cuando mode==="edit" (para cargar/guardar sus 3 notas). */
  aseoId?: number | null;
}

const CleaningFields: React.FC<CleaningFieldsProps> = ({
  form,
  startThirdWatch,
  endThirdWatch,
  modoAsignacion,
  aseoCostPreset,
  onAseoPresetChange,
  aseoCostoTotal,
  esPasado,
  esFuturo,
  // loadingAseadoras se dejó de usar acá (el multi-select del reparto no
  // muestra spinner propio); la prop sigue llegando por compat del caller.
  loadingAseadoras: _loadingAseadoras,
  aseadorOptions,
  ingreso,
  range,
  isDayFullyOccupied,
  esVinculado,
  mode,
  aseoId,
}) => {
  return (
    <>
      <Divider orientation="left">Aseo</Divider>

      {/*
       * Entrada / Salida — misma estructura espacial que
       * arriendo/bloqueo/mantención. Por defecto el aseo es
       * intra-día (mismo día en entrada y salida), pero el
       * admin puede mover ambas fechas si necesita extender
       * el aseo a otro día — útil cuando el aseo real arranca
       * tarde de una fecha y se cierra al día siguiente, o
       * cuando hay que reprogramar la fecha completa.
       */}
      <Row gutter={24} style={{ marginTop: 8 }}>
        <Col xs={24} lg={12}>
          <Text strong style={{ display: "block", marginBottom: 6 }}>
            Entrada
          </Text>
          <Row gutter={8}>
            <Col xs={24} sm={10}>
              <Form.Item
                name="ingreso"
                label="Fecha"
                rules={[
                  {
                    required: true,
                    message: "Fecha de inicio es obligatoria",
                  },
                ]}
              >
                <DatePicker
                  format="DD-MM-YYYY"
                  style={{ width: "100%" }}
                  placeholder="Fecha"
                  disabledDate={(current) => {
                    if (!current) return false;
                    if (!range || !isDayFullyOccupied) return false;
                    return isDayFullyOccupied(
                      range.propertyId,
                      current.format("YYYY-MM-DD"),
                    );
                  }}
                  onChange={(d) => {
                    // Aseo es intra-día: si el admin mueve la fecha
                    // de entrada, la salida debe acompañar. Sin esto,
                    // mover ingreso al futuro deja salida < ingreso
                    // (incoherente). El onChange solo dispara en
                    // interacción manual — no se invoca al cargar el
                    // form en modo edit, así que multi-día previo no
                    // se pisa por initial load.
                    if (d && dayjs.isDayjs(d)) {
                      form.setFieldsValue({ salida: d });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={7}>
              <StartThirdField />
            </Col>
            <Col xs={12} sm={7}>
              <StartTimeField third={startThirdWatch} />
            </Col>
          </Row>
        </Col>

        <Col xs={24} lg={12}>
          <Text strong style={{ display: "block", marginBottom: 6 }}>
            Salida
          </Text>
          <Row gutter={8}>
            <Col xs={24} sm={10}>
              <Form.Item
                name="salida"
                label="Fecha"
                rules={[
                  {
                    required: true,
                    message: "Fecha de salida es obligatoria",
                  },
                ]}
              >
                <DatePicker
                  format="DD-MM-YYYY"
                  style={{ width: "100%" }}
                  placeholder="Fecha"
                  disabledDate={(current) => {
                    if (!current) return false;
                    const i = ingreso;
                    if (i) {
                      const iDay = dayjs.isDayjs(i) ? i : dayjs(i);
                      if (current.isBefore(iDay, "day")) return true;
                    }
                    if (range && isDayFullyOccupied) {
                      return isDayFullyOccupied(
                        range.propertyId,
                        current.format("YYYY-MM-DD"),
                      );
                    }
                    return false;
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={7}>
              <EndThirdField />
            </Col>
            <Col xs={12} sm={7}>
              <EndTimeField third={endThirdWatch} />
            </Col>
          </Row>
        </Col>
      </Row>

      {/* ── Asignación ── */}
      <Card
        size="small"
        style={{ marginBottom: 12 }}
        title={
          <Space>
            <Text strong>Asignación</Text>
            <Tooltip title="¿Quién va a hacer este aseo? 'Designado' obliga a elegir un aseador específico; 'Abierto' deja el aseo disponible para que cualquier aseador se lo auto-asigne desde su panel.">
              <InfoCircleOutlined style={{ opacity: 0.7 }} />
            </Tooltip>
          </Space>
        }
      >
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="modoAsignacion"
              label="Modo de asignación"
              initialValue="designado"
              tooltip="Designado = tú eliges a las aseadoras. Abierto = la tarea queda en una bolsa y el primer aseador disponible la toma."
              style={{ marginBottom: 0 }}
            >
              <Select>
                <Select.Option value="designado">
                  Designado (aseador específico)
                </Select.Option>
                <Select.Option value="abierto">
                  Abierto (cualquier aseador disponible)
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>

          {modoAsignacion === "designado" && (
            <Col xs={24} md={12}>
              {/* Round 2026-08-25 — asignación 1..N aseadoras + reparto del
                  pago (partes iguales por defecto, editable). El componente
                  gobierna idAseadores/asignaciones y mantiene los espejos
                  legacy idAseador/nombreAseador que el payload sigue usando.
                  El total del reparto es el subitem "Pago aseador"
                  (aseoCustomAseador). */}
              <RepartoAseadorasField
                form={form}
                aseadoras={aseadorOptions.map((o: any) => ({
                  id: Number(o.value),
                  nombre: String(o.label ?? `#${o.value}`),
                }))}
                totalFieldName="aseoCustomAseador"
                // Aviso «ya tiene otro aseo ese día» (round 2026-09-11): mira
                // las fechas del aseo; en edición se excluye el aseo mismo.
                avisoMismoDia={{
                  campoDesde: "ingreso",
                  campoHasta: "salida",
                  excluirAseoId: mode === "edit" ? (aseoId ?? null) : null,
                }}
              />
            </Col>
          )}
        </Row>
      </Card>

      {/* ── Desglose del costo del aseo ── */}
      <Card
        size="small"
        style={{ marginBottom: 12, background: "#FAFAFA" }}
        title={
          <Space>
            <Text strong>Desglose del costo del aseo</Text>
            <Tooltip title="Descompone el costo en 4 subitems (como en la propiedad): pago aseador, transporte, materiales y lavandería. Por defecto usa los valores de la propiedad; con 'Personalizada' puedes sobreescribirlos solo para este aseo.">
              <InfoCircleOutlined style={{ opacity: 0.7 }} />
            </Tooltip>
          </Space>
        }
      >
        <Form.Item
          name="aseoCostPreset"
          label="Modo"
          initialValue="default"
          style={{ marginBottom: 12 }}
        >
          {/* Form.Item conserva el onChange propio del child (lo invoca
              después de actualizar el store): acá va el snapshot/repintado
              manual del modal — round 2026-08-02. */}
          <Select
            style={{ maxWidth: 320 }}
            onChange={(v: "default" | "custom") => onAseoPresetChange?.(v)}
          >
            <Select.Option value="default">
              Por defecto (valores de la propiedad)
            </Select.Option>
            <Select.Option value="custom">
              Personalizada (editar subitems)
            </Select.Option>
          </Select>
        </Form.Item>

        <Row gutter={12}>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              name="aseoCustomAseador"
              label="Pago aseador"
              tooltip="Lo que Aloja Fácil Empresa le paga al aseador por este aseo. Sin IVA (persona natural). Ejemplo: $15.000."
              style={{ marginBottom: 8 }}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                formatter={clpInputFormatter} parser={clpInputParser}
                readOnly={aseoCostPreset !== "custom"}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              name="aseoCustomTransporteTotal"
              label="Transporte (total)"
              tooltip="Costo total (con IVA) de transporte para este aseo. Neto = total/1.19 se le acredita a Aloja Fácil Empresa. Ejemplo: $5.950."
              style={{ marginBottom: 8 }}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                formatter={clpInputFormatter} parser={clpInputParser}
                readOnly={aseoCostPreset !== "custom"}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              name="aseoCustomMaterialesTotal"
              label="Materiales (total)"
              tooltip="Costo total (con IVA) de materiales para este aseo. Neto = total/1.19 se le acredita a Aloja Fácil Empresa. Ejemplo: $4.760."
              style={{ marginBottom: 8 }}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                formatter={clpInputFormatter} parser={clpInputParser}
                readOnly={aseoCostPreset !== "custom"}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              name="aseoCustomLavanderiaTotal"
              label="Lavandería (total)"
              tooltip={
                <div>
                  <p style={{ margin: 0 }}>
                    Costo total (con IVA) de lavandería para este
                    aseo. Ejemplo: $5.950.
                  </p>
                  <p style={{ marginTop: 6 }}>
                    <strong>Destino del dinero según la propiedad:</strong>
                  </p>
                  <p style={{ margin: 0 }}>
                    · Si la propiedad apunta a una <strong>Lavandería
                    AF interna</strong> (con su propia contabilidad),
                    este monto va a esa lavandería y NO entra al
                    ingreso de AF Empresa.
                  </p>
                  <p style={{ marginTop: 6 }}>
                    · Si la propiedad apunta a la <strong>Lavandería
                    General Externa</strong>, el monto se considera
                    ingreso de AF Empresa, y el costo real al
                    proveedor externo se anota como gasto manual en
                    /finanzas-empresa.
                  </p>
                </div>
              }
              style={{ marginBottom: 8 }}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                formatter={clpInputFormatter} parser={clpInputParser}
                readOnly={aseoCostPreset !== "custom"}
              />
            </Form.Item>
          </Col>
        </Row>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Costo total del aseo ={" "}
          <strong>
            ${Math.round(aseoCostoTotal).toLocaleString("es-CL")}
          </strong>
        </Text>

        {/* Mirrors escondidos que alimentan el payload legacy:
            sueldoAseador = aseoCustomAseador, costoLimpieza = suma. */}
        <Form.Item name="sueldoAseador" hidden>
          <InputNumber />
        </Form.Item>
        <Form.Item name="costoLimpieza" hidden>
          <InputNumber />
        </Form.Item>
      </Card>

      {/* ── Timing ── */}
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            name="urgencia"
            label="Urgencia"
            tooltip="AM = propiedad vacía, sin check-out previo, se puede hacer desde cualquier hora de la mañana · 11:00 = hubo check-out a 10:59 y aseador parte a 11:00, SIN apuro (no hay huésped después) · Crítico 11:00 = check-out 10:59 + huésped entrando a 15:00, contra el tiempo · PM = la propiedad estuvo ocupada en la mañana, aseo se hace en la tarde."
            rules={[
              {
                required: true,
                message: "Seleccione la urgencia",
              },
            ]}
          >
            <Select>
              <Select.Option value="am">AM</Select.Option>
              <Select.Option value="11:00">11:00</Select.Option>
              <Select.Option value="critico 11:00">Crítico</Select.Option>
              <Select.Option value="pm">PM</Select.Option>
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name="estado"
            label="Estado"
            tooltip={
              <div>
                <p style={{ margin: 0 }}>
                  <strong>Pendiente</strong> = aún no empieza ·{" "}
                  <strong>En proceso</strong> = el aseador está
                  trabajando · <strong>Completado</strong> = aseo
                  terminado · <strong>Pagado</strong> = ya se le
                  liquidó al aseador ·{" "}
                  <strong>Cancelado</strong> = no se realizó.
                </p>
                <p style={{ marginTop: 6 }}>
                  <strong>Cadencia de pago:</strong> los aseos en
                  estado "Completado" con aseador asignado se
                  liquidan los <strong>lunes</strong>, cubriendo la
                  semana anterior completa (Lun → Dom). El admin
                  dispara el pago manualmente con el botón
                  "Marcar todo pagado" en /aseos. Marcar "Pagado"
                  acá fuerza el estado para este aseo individual.
                </p>
              </div>
            }
            rules={[{ required: true, message: "Seleccione el estado" }]}
          >
            <Select>
              <Select.Option value="pendiente" disabled={esPasado}>
                Pendiente
              </Select.Option>
              <Select.Option value="en_proceso" disabled={esPasado}>
                En proceso
              </Select.Option>
              <Select.Option value="completado" disabled={esFuturo}>
                Completado
              </Select.Option>
              <Select.Option value="pagado" disabled={esFuturo}>
                Pagado
              </Select.Option>
              <Select.Option value="cancelado" disabled={esFuturo}>
                Cancelado
              </Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {/* ── Financiamiento del aseo ──
          Reemplaza al trío Rol AF / Pagado por / Pagado a.
          El destinatario del pago SIEMPRE es el aseador (ya lo sabemos
          por `idAseador`), así que "Pagado a" no aporta información
          y se elimina. Sólo necesitamos: ¿con qué bolsillo se paga? */}
      <Card
        size="small"
        style={{ marginBottom: 12 }}
        title={
          <Space>
            <Text strong>Financiamiento del aseo</Text>
            <Tooltip title="¿Con qué bolsillo se paga este aseo? El dinero SIEMPRE llega al aseador (ya está designado arriba); acá sólo configuramos de dónde sale el pago.">
              <InfoCircleOutlined style={{ opacity: 0.7 }} />
            </Tooltip>
          </Space>
        }
      >
        {/* Round 2026-05-13 — el aseo ya no lleva `rolAF` ni `pagado_a`.
            El único campo de flujo financiero es `pagado_por.tipo` (Quién
            paga). Destinatarios son implícitos:
              • Aseador asignado SIEMPRE recibe `sueldo_aseador`.
              • Lavandería asociada SIEMPRE recibe `lavandería`
                (auto-Lavado en /lavanderia).
              • AF retiene `transporte + materiales` si paga propietario o
                arrendatario; egresa los 4 subitems si AF paga directo.
            Ver docs/flujo-financiero.md §6.5. */}
        <Form.Item
          name={["pagado_por", "tipo"]}
          label="Quién paga el aseo"
          initialValue={0}
          rules={[
            {
              required: true,
              message: "Seleccione quién paga el aseo",
            },
          ]}
          tooltip={
            <div>
              <p style={{ margin: 0 }}>
                <strong>Aloja Fácil:</strong> AF Empresa paga el aseo
                de su caja. Egreso = los 4 subitems (pago aseador +
                transporte + materiales + lavandería).
              </p>
              <p style={{ marginTop: 6 }}>
                <strong>Propietario:</strong> el dueño paga el aseo
                (típicamente porque AF se lo cobra o se descuenta de
                la liquidación). AF retiene{" "}
                <em>transporte + materiales</em> como ingreso; el
                resto es pass-through.
              </p>
              <p style={{ marginTop: 6 }}>
                <strong>Arrendatario:</strong> el huésped paga el aseo
                (incluido en la reserva). AF retiene{" "}
                <em>transporte + materiales</em>; el resto es pass-through.
              </p>
              <p style={{ marginTop: 6 }}>
                <strong>Regla:</strong> el aseo de un arriendo lo paga el
                Arrendatario (su limpieza ya se le descuenta al cliente en la
                liquidación). La opción "Propietario/Cliente" es solo para aseos
                <em> aledaños</em> (sin arriendo) que el cliente pide aparte —
                cobrarle un aseo de arriendo sería doble cobro. Ver
                flujo-financiero.md §6.4.
              </p>
            </div>
          }
          // Cuando el aseo es de un arriendo, mostramos el motivo de por qué
          // "Propietario/Cliente" queda deshabilitado (su limpieza ya se cobró).
          extra={
            esVinculado ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Este aseo es de un arriendo: su limpieza ya se le descuenta al
                cliente en la liquidación, por eso "Propietario/Cliente paga"
                está deshabilitado (sería doble cobro). Usa Aloja Fácil o
                Arrendatario.
              </Text>
            ) : undefined
          }
        >
          <Select style={{ maxWidth: 360 }}>
            <Select.Option value={0}>Aloja Fácil (egreso AF)</Select.Option>
            {/* tipo=1 (Propietario/Cliente) solo válido en aseos ALEDAÑOS;
                en un aseo vinculado sería doble cobro → deshabilitado. */}
            <Select.Option value={1} disabled={esVinculado}>
              Propietario
            </Select.Option>
            <Select.Option value={2}>Arrendatario</Select.Option>
          </Select>
        </Form.Item>
      </Card>

      {/* ── Notas del aseo ─────────────────────────────────────────────
          Round 2026-07-06: reemplaza el antiguo textarea "Observaciones"
          (que escribía la MISMA columna que la "Nota para el aseador").
          - EDICIÓN (el aseo ya tiene id): editor completo de las 3 notas con
            texto + fotos/videos/documentos (guardado inmediato por nota).
          - CREACIÓN (aún sin id): solo el texto de "Nota para el aseador"
            (mapea a `observaciones`, que el payload del calendario ya envía);
            los archivos y las otras notas se agregan editando el aseo. */}
      <Row gutter={16}>
        <Col xs={24} md={24}>
          {mode === "edit" && aseoId != null ? (
            <div
              style={{
                padding: 16,
                border: "1px solid #d9d9d9",
                borderRadius: 8,
                background: "#fff",
              }}
            >
              <div style={{ marginBottom: 4 }}>
                <span
                  style={{ fontWeight: 700, fontSize: 14, color: "#1f2937" }}
                >
                  📝 Notas del aseo
                </span>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  Nota privada (solo admin), nota para el aseador y observaciones
                  del aseador. Cada una admite texto + fotos, videos o
                  documentos.
                </div>
              </div>
              <AseoNotesSection aseoId={aseoId} />
            </div>
          ) : (
            <Form.Item
              name="observaciones"
              label="Nota para el aseador (instrucciones)"
              tooltip="Instrucciones que verá el aseador para este aseo. Al crear solo se guarda el texto; para adjuntar fotos/videos/documentos o usar la nota privada y las observaciones del aseador, edita el aseo después de crearlo."
            >
              <Input.TextArea
                rows={3}
                placeholder="Instrucciones para el aseador…"
              />
            </Form.Item>
          )}
        </Col>
      </Row>
    </>
  );
};

export default CleaningFields;
